/** Data hooks for Phase 9: help and support, the public status page, VAT checks, mailboxes. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";

// ---- help and support ----
export interface SupportMessage { at: string; from: "customer" | "support"; author: string; body: string }
export interface SupportRequest {
  id: string; number: string; subject: string; category: string; priority: "normal" | "high" | "urgent";
  status: "open" | "answered" | "closed"; requester_name: string; requester_email: string; messages: SupportMessage[];
  first_reply_due_at: string | null; first_replied_at: string | null; overdue: boolean; created_at: string; closed_at: string | null;
}
export const useSupportRequests = () =>
  useQuery({ queryKey: ["support"], queryFn: () => api<SupportRequest[]>("GET", "/support"), refetchInterval: 30_000 });
export function useSupportAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => api<SupportRequest>("POST", path, body ?? {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["support"] }); },
  });
}

// ---- status page ----
export type ServiceState = "operational" | "degraded" | "outage" | "maintenance";
export interface StatusIncident {
  id: string; title: string; body: string; severity: "maintenance" | "minor" | "major"; starts_at: string;
  resolved_at: string | null; updates: { at: string; body: string }[];
}
export interface PlatformStatus {
  status: ServiceState; summary: string; checked_at: string;
  components: { key: string; name: string; status: ServiceState; detail: string }[];
  incidents: StatusIncident[]; recent: StatusIncident[];
}
async function fetchStatus(): Promise<PlatformStatus> {
  const res = await fetch("/api/v1/public/status");
  if (!res.ok) throw new Error(`Status unavailable (${res.status})`);
  return (await res.json()) as PlatformStatus;
}
export const useStatus = (poll = 60_000) =>
  useQuery({ queryKey: ["public-status"], queryFn: fetchStatus, refetchInterval: poll, retry: 1 });
